import {
  isSequential,
  isFF,
  pins,
  sourceKey,
  allSignals,
  activationFor,
} from "./registry";
import { evaluate, nextState, inv, bit, merge } from "./logic";
import { validate } from "./validation";
export const roundTime = (t) => Math.round(t * 1e6) / 1e6;
export function clockValue(c, t) {
  const { period, duty, phase, initial } = c.config;
  if (t < phase) return initial;
  const r = roundTime((((t - phase) % period) + period) % period),
    first = roundTime(period * (initial === 1 ? duty : 1 - duty));
  return r < first ? initial : 1 - initial;
}
export function inputValue(input, t, step) {
  if (input.events) {
    let value = "X";
    for (const e of input.events) {
      if (e.time > t + 1e-7) break;
      value = e.value;
    }
    return bit(value);
  }
  const index = Math.floor((t + 1e-7) / step);
  return bit(input.values?.[Math.min(index, (input.values?.length || 1) - 1)]);
}
export function eventTimes(project) {
  const end = project.timing.duration,
    times = new Set([0, end]);
  for (const i of project.inputs) {
    if (i.events)
      i.events.forEach((e) => {
        if (e.time >= 0 && e.time < end) times.add(roundTime(e.time));
      });
    else
      for (let k = 1; k * project.timing.step < end; k++)
        times.add(roundTime(k * project.timing.step));
  }
  for (const c of project.components.filter((c) => c.type === "CLOCK")) {
    const { period, duty, phase, initial } = c.config,
      first = period * (initial === 1 ? duty : 1 - duty);
    for (let k = 0; phase + k * period < end; k++) {
      const a = roundTime(phase + k * period + first),
        b = roundTime(phase + (k + 1) * period);
      if (a < end) times.add(a);
      if (b < end) times.add(b);
      if (times.size > 20000)
        throw new Error(
          "Too many clock events. Increase the periods or shorten the duration (maximum 20,000 timestamps).",
        );
    }
  }
  return [...times].sort((a, b) => a - b);
}
export function simulate(project) {
  const checked = validate(project);
  if (!checked.valid)
    throw new Error(
      checked.issues
        .filter((i) => i.severity === "error")
        .map((i) => i.message)
        .join("\n"),
    );
  const states = Object.fromEntries(
    project.components
      .filter(isSequential)
      .map((c) => [c.id, c.config.initial]),
  );
  const sequential = project.components.filter(isSequential),
    clocks = project.components.filter((c) => c.type === "CLOCK");
  const signals = allSignals(project),
    times = eventTimes(project),
    samples = [],
    trace = [],
    warnings = [];
  const read = (source, values) =>
    !source
      ? "X"
      : "constant" in source
        ? source.constant
        : bit(values[sourceKey(source)]);
  const getInputs = (c, values) =>
    Object.fromEntries(
      pins(c).inputs.map((pin) => [
        pin.id,
        read(checked.drivers.get(`${c.id}:${pin.id}`), values),
      ]),
    );
  function settleComb(t) {
    const values = { "const:0": 0, "const:1": 1, "const:X": "X" };
    project.inputs.forEach(
      (i) => (values[`input:${i.id}`] = inputValue(i, t, project.timing.step)),
    );
    clocks.forEach((c) => (values[`${c.id}:CLK`] = clockValue(c, t)));
    sequential.forEach((c) => {
      values[`${c.id}:Q`] = states[c.id];
      values[`${c.id}:Qn`] = inv(states[c.id]);
    });
    checked.order.forEach((c) => {
      const out = evaluate(c, getInputs(c, values));
      Object.entries(out).forEach(([pin, v]) => (values[`${c.id}:${pin}`] = v));
    });
    sequential.forEach((c) =>
      pins(c)
        .inputs.filter((pin) => !pin.control)
        .forEach((pin) => {
          values[`pin:${c.id}:${pin.id}`] = read(
            checked.drivers.get(`${c.id}:${pin.id}`),
            values,
          );
        }),
    );
    return values;
  }
  let previous = null;
  for (const time of times.slice(0, -1)) {
    let current = settleComb(time),
      prior = previous || current,
      stable = false;
    const seen = new Set();
    for (let delta = 0; delta < 128; delta++) {
      const updates = {},
        pending = [];
      for (const c of sequential) {
        const control = c.type === "SR_LATCH" ? "EN" : "CLK";
        const before = read(checked.drivers.get(`${c.id}:${control}`), prior),
          now = read(checked.drivers.get(`${c.id}:${control}`), current);
        const active = activationFor(project, c).trigger === "POS" ? 1 : 0;
        let cause = "",
          uncertain = false;
        if (isFF(c)) {
          if (previous === null && delta === 0) continue;
          if (before === 1 - active && now === active)
            cause = active ? "rising edge" : "falling edge";
          else if (before !== now && (before === "X" || now === "X")) {
            cause = "uncertain clock transition";
            uncertain = true;
          } else continue;
        } else {
          if (now === 1 - active) continue;
          cause = active ? "enabled HIGH level" : "enabled LOW level";
          uncertain = now === "X";
        }
        // Edge devices sample the stable left limit. All devices in a delta
        // compute before any state is committed. Latches use current inputs.
        const inputs = getInputs(c, isFF(c) ? prior : current),
          q = states[c.id],
          next = nextState(c, inputs, q);
        const value = uncertain ? merge([q, next.value]) : next.value;
        if (value !== q) updates[c.id] = value;
        if (isFF(c) || value !== q || next.forbidden)
          pending.push({
            time,
            delta,
            component: c.id,
            name: c.name,
            kind: c.type,
            inputs,
            previous: q,
            next: value,
            inverse: inv(value),
            clock: now,
            cause,
            rule: next.rule,
            forbidden: next.forbidden,
          });
      }
      trace.push(...pending);
      if (!Object.keys(updates).length) {
        stable = true;
        break;
      }
      Object.assign(states, updates);
      const signature = JSON.stringify(states);
      if (seen.has(signature)) break;
      seen.add(signature);
      prior = current;
      current = settleComb(time);
    }
    if (!stable) {
      throw new Error(
        `Unstable sequential/transparent-latch feedback at t=${time}. Break the transparent feedback path or change its enable. No answer has been produced.`,
      );
    }
    samples.push({ time, values: current });
    previous = current;
  }
  for (const c of project.components.filter((c) => c.type === "ENCODER")) {
    const count = samples.filter((s) => s.values[`${c.id}:V`] !== 1).length;
    if (count)
      warnings.push(
        `${c.name}: ${count} interval(s) violate the one-hot requirement; code outputs are X when input is invalid.`,
      );
  }
  const forbidden = trace.filter((t) => t.forbidden);
  if (forbidden.length)
    warnings.push(
      `${forbidden.length} SR forbidden-state event(s) used the configured classroom policy. See the transition explanations.`,
    );
  return {
    times,
    samples,
    signals,
    trace,
    warnings,
    duration: project.timing.duration,
  };
}
export function valueAt(result, key, time) {
  let lo = 0,
    hi = result.samples.length - 1;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (result.samples[mid].time <= time + 1e-7) lo = mid;
    else hi = mid - 1;
  }
  return result.samples[lo]?.values[key] ?? "X";
}
export function explainAt(project, result, time) {
  const snap = result.samples.reduce(
      (a, s) => (s.time <= time ? s : a),
      result.samples[0],
    ),
    checked = validate(project);
  const changes = result.trace.filter(
    (t) => Math.abs(t.time - snap.time) < 1e-6,
  );
  const combinational = checked.order.map((c) => ({
    component: c.id,
    name: c.name,
    kind: c.type,
    inputs: Object.fromEntries(
      pins(c).inputs.map((p) => [
        p.id,
        snap.values[sourceKey(checked.drivers.get(`${c.id}:${p.id}`))] ?? "X",
      ]),
    ),
    outputs: Object.fromEntries(
      pins(c).outputs.map((p) => [p.id, snap.values[`${c.id}:${p.id}`]]),
    ),
  }));
  return { time: snap.time, changes, combinational };
}
export function checkAnswer(result, answers, signalIds) {
  const cells = [];
  for (const id of signalIds) {
    const answer = answers[id];
    result.samples.forEach((sample, i) => {
      const end = result.times[i + 1];
      // UI cells map exactly to event intervals. Event-list answers are
      // compared at every submitted boundary as well as simulator boundaries.
      const boundaries = [
        sample.time,
        ...(Array.isArray(answer) && typeof answer[0] === "object"
          ? answer
              .filter((e) => e.time > sample.time && e.time < end)
              .map((e) => e.time)
          : []),
      ];
      const correct = boundaries.every((t) => {
        let given;
        if (Array.isArray(answer) && typeof answer[0] === "object") {
          for (const e of answer) {
            if (e.time <= t) given = e.value;
          }
        } else given = answer?.[i];
        return (
          given !== undefined &&
          given !== null &&
          bit(given) === sample.values[id]
        );
      });
      cells.push({
        signal: id,
        index: i,
        start: sample.time,
        end,
        correct,
        expected: sample.values[id],
      });
    });
  }
  return {
    cells,
    correct: cells.filter((c) => c.correct).length,
    total: cells.length,
  };
}
